import { useState } from 'react';
import { MessageCircle, X } from 'lucide-react';

const WHATSAPP_NUMBER = '27767111941';

const quickMessages = [
  { label: 'General Enquiry', message: 'Hi MeritumPraxis, I have a question about your platform.' },
  { label: 'Pricing Info', message: 'Hi MeritumPraxis, I would like to know more about your pricing plans.' },
  { label: 'Technical Support', message: 'Hi MeritumPraxis, I need help with a technical issue on the platform.' },
  { label: 'Organization Enquiry', message: 'Hi MeritumPraxis, I represent an organization and would like to learn more about accessing candidate profiles.' },
];

export function WhatsAppButton() {
  const [isOpen, setIsOpen] = useState(false);

  const getUser = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  };

  const openWhatsApp = (message) => {
    const user = getUser();
    const userInfo = user.name ? `\n\nName: ${user.name}\nEmail: ${user.email || ''}` : '';
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message + userInfo)}`;
    window.open(url, '_blank');
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="whatsapp-widget">
      {/* Quick message popup */}
      {isOpen && (
        <div className="mb-4 bg-white rounded-2xl shadow-2xl border border-slate-200 w-80 overflow-hidden animate-in slide-in-from-bottom-4 duration-200" data-testid="whatsapp-popup">
          <div className="bg-[#075E54] px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle size={20} className="text-white" />
              </div>
              <div>
                <p className="text-white font-heading font-semibold text-sm">MeritumPraxis</p>
                <p className="text-white/70 font-body text-xs">Typically replies within an hour</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white transition p-1" data-testid="whatsapp-close-btn">
              <X size={18} />
            </button>
          </div>

          <div className="p-4">
            <div className="bg-[#ECE5DD] rounded-lg p-3 mb-4">
              <p className="font-body text-sm text-slate-700">Hi there! How can we help you today? Choose an option below or type your own message.</p>
            </div>

            <div className="space-y-2">
              {quickMessages.map((qm, i) => (
                <button
                  key={i}
                  onClick={() => openWhatsApp(qm.message)}
                  className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:bg-[#25D366]/5 hover:border-[#25D366]/30 transition-colors group"
                  data-testid={`whatsapp-quick-msg-${i}`}
                >
                  <span className="font-body text-sm text-slate-700 group-hover:text-[#075E54] font-medium">{qm.label}</span>
                </button>
              ))}
            </div>

            <button
              onClick={() => openWhatsApp('Hi MeritumPraxis!')}
              className="w-full mt-3 bg-[#25D366] text-white py-3 rounded-lg font-body font-semibold text-sm hover:bg-[#20BD5A] transition-colors flex items-center justify-center gap-2"
              data-testid="whatsapp-open-chat-btn"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Start Chat
            </button>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 ${
          isOpen ? 'bg-slate-600 hover:bg-slate-700' : 'bg-[#25D366] hover:bg-[#20BD5A]'
        }`}
        data-testid="whatsapp-fab"
        title="Chat with us on WhatsApp"
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </button>
    </div>
  );
}
